import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from './Button';
import { Card } from './Card';
import type { IconType } from './Chip';
import { Pagination } from './Pagination';
import { RowAction, RowActions } from './RowActions';
import { Skeleton, SkeletonRegion } from './Skeleton';
import { StateBlock } from './StateBlock';

export interface Column<T> {
    id: string;
    header: string;
    cell: (row: T) => ReactNode;
    /** Presence makes the column sortable. */
    sortValue?: (row: T) => string | number;
    align?: 'start' | 'end';
    /** CSS width, e.g. '8rem'. */
    width?: string;
    /** primary = card title, secondary = stacked lines, meta = "label value" pairs. */
    priority: 'primary' | 'secondary' | 'meta';
}

export interface DataTableProps<T> {
    rows: T[];
    columns: Column<T>[];
    getRowId: (row: T) => string;
    /** The first column becomes a link to this URL. */
    rowHref?: (row: T) => string;
    rowActions?: (row: T) => RowAction[];
    /** Accessible name of the row's action menu. */
    rowLabel?: (row: T) => string;
    state: 'loading' | 'error' | 'ready';
    onRetry?: () => void;
    empty: { icon: IconType; title: string; body?: string; action?: ReactNode };
    errorTitle?: string;
    /** Visually hidden table caption. */
    caption: string;
    pageSize?: number;
    /** Query parameter holding the page (default "page"). */
    pageParam?: string;
    /** Width from which the table replaces the card list (default md). Wide tables set a larger one. */
    tableFrom?: 'md' | 'lg' | 'xl' | '2xl';
}

// Full class names so Tailwind can see them.
const TABLE_FROM = {
    md: { table: 'md:block', cards: 'md:hidden' },
    lg: { table: 'lg:block', cards: 'lg:hidden' },
    xl: { table: 'xl:block', cards: 'xl:hidden' },
    '2xl': { table: '2xl:block', cards: '2xl:hidden' },
} as const;

type Sort = { id: string; dir: 'ascending' | 'descending' } | null;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** One column definition renders a table from `tableFrom` (768 px by default) and a card list below. Filtering is up to the caller. */
export function DataTable<T>({
    rows,
    columns,
    getRowId,
    rowHref,
    rowActions,
    rowLabel,
    state,
    onRetry,
    empty,
    errorTitle,
    caption,
    pageSize = 25,
    pageParam = 'page',
    tableFrom = 'md',
}: DataTableProps<T>) {
    const { t } = useTranslation();
    const [sort, setSort] = useState<Sort>(null);
    const [params, setParams] = useSearchParams();

    const sorted = useMemo(() => {
        const col = sort && columns.find((c) => c.id === sort.id);
        if (!sort || !col?.sortValue) return rows;
        const value = col.sortValue;
        const factor = sort.dir === 'ascending' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = value(a);
            const y = value(b);
            return factor * (typeof x === 'number' && typeof y === 'number' ? x - y : collator.compare(String(x), String(y)));
        });
    }, [rows, columns, sort]);

    const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const page = Math.min(Math.max(1, Number(params.get(pageParam)) || 1), pages);
    const visible = sorted.slice((page - 1) * pageSize, page * pageSize);

    const goTo = (next: number) =>
        setParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                if (next <= 1) p.delete(pageParam);
                else p.set(pageParam, String(next));
                return p;
            },
            { replace: true }
        );

    const toggleSort = (id: string) =>
        setSort((s) => (s?.id !== id ? { id, dir: 'ascending' } : s.dir === 'ascending' ? { id, dir: 'descending' } : null));

    if (state === 'loading') {
        return (
            <SkeletonRegion>
                <Card>
                    {Array.from({ length: 6 }, (_, i) => (
                        <div key={i} className="flex items-center gap-4 border-b border-hairline py-4 last:border-0">
                            <Skeleton className="h-3 w-[40%]" />
                            <Skeleton className="h-3 w-[25%]" />
                            <Skeleton className="ml-auto h-3 w-[15%]" />
                        </div>
                    ))}
                </Card>
            </SkeletonRegion>
        );
    }
    if (state === 'error') {
        return (
            <Card padding="none">
                <StateBlock
                    variant="error"
                    title={errorTitle ?? t('ui.loadFailed')}
                    body={t('ui.checkConnection')}
                    action={onRetry && <Button onClick={onRetry}>{t('ui.retry')}</Button>}
                />
            </Card>
        );
    }
    if (rows.length === 0) {
        return (
            <Card padding="none">
                <StateBlock variant="empty" {...empty} />
            </Card>
        );
    }

    const linked = (row: T, content: ReactNode) =>
        rowHref ? (
            <Link to={rowHref(row)} className="font-semibold text-strong hover:underline">
                {content}
            </Link>
        ) : (
            content
        );
    const actionsFor = (row: T) => {
        const actions = rowActions?.(row);
        return actions && actions.length > 0 ? <RowActions actions={actions} label={rowLabel?.(row) ?? getRowId(row)} /> : null;
    };
    const primary = columns.filter((c) => c.priority === 'primary');
    const secondary = columns.filter((c) => c.priority === 'secondary');
    const meta = columns.filter((c) => c.priority === 'meta');
    /** Many pairs run together inline; past three, a card shows them as a two-column grid with the label above the value. */
    const stacked = meta.length > 3;

    return (
        <div>
            <div className={clsx('hidden overflow-x-auto rounded-card bg-surface shadow-hairline', TABLE_FROM[tableFrom].table)}>
                <table className="w-full text-small">
                    <caption className="sr-only">{caption}</caption>
                    <thead>
                        <tr className="h-11 border-b border-hairline">
                            {columns.map((c) => {
                                const dir = sort?.id === c.id ? sort.dir : undefined;
                                return (
                                    <th
                                        key={c.id}
                                        scope="col"
                                        aria-sort={c.sortValue ? (dir ?? 'none') : undefined}
                                        style={{ width: c.width }}
                                        className={clsx('px-4 text-caption font-medium text-muted', c.align === 'end' ? 'text-end' : 'text-start')}
                                    >
                                        {c.sortValue ? (
                                            <button
                                                type="button"
                                                onClick={() => toggleSort(c.id)}
                                                className={clsx('inline-flex min-h-control items-center gap-1', c.align === 'end' && 'flex-row-reverse')}
                                            >
                                                {c.header}
                                                {dir === 'ascending' && <ChevronUpIcon className="size-4" aria-hidden="true" />}
                                                {dir === 'descending' && <ChevronDownIcon className="size-4" aria-hidden="true" />}
                                            </button>
                                        ) : (
                                            c.header
                                        )}
                                    </th>
                                );
                            })}
                            {rowActions && (
                                <th scope="col" className="w-16 px-4">
                                    <span className="sr-only">{t('ui.actions')}</span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={getRowId(row)} className="min-h-14 border-b border-hairline last:border-0 hover:bg-hover">
                                {columns.map((c, i) => (
                                    <td key={c.id} className={clsx('px-4 py-3 align-middle', c.align === 'end' && 'text-end tabular-nums')}>
                                        {i === 0 ? linked(row, c.cell(row)) : c.cell(row)}
                                    </td>
                                ))}
                                {rowActions && <td className="px-4 text-end">{actionsFor(row)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className={clsx('flex flex-col gap-3', TABLE_FROM[tableFrom].cards)}>
                {visible.map((row) => (
                    <Card as="li" key={getRowId(row)} className="sm:p-4">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 text-heading text-strong">
                                {primary.map((c, i) => (
                                    <div key={c.id}>{i === 0 ? linked(row, c.cell(row)) : c.cell(row)}</div>
                                ))}
                            </div>
                            <div className="-mr-2 -mt-1 shrink-0">{actionsFor(row)}</div>
                        </div>
                        {secondary.length > 0 && (
                            <div className="mt-2 flex flex-col gap-1 text-small">
                                {secondary.map((c) => (
                                    <div key={c.id}>{c.cell(row)}</div>
                                ))}
                            </div>
                        )}
                        {meta.length > 0 && (
                            <dl className={clsx('mt-2 gap-x-4 text-caption text-muted', stacked ? 'grid grid-cols-2 gap-y-2' : 'flex flex-wrap gap-y-1')}>
                                {meta.map((c) => (
                                    <div key={c.id} className={clsx('min-w-0', stacked ? 'flex flex-col' : 'flex gap-1')}>
                                        <dt>{c.header}</dt>
                                        <dd className="text-body">{c.cell(row)}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                    </Card>
                ))}
            </ul>

            {rows.length > pageSize && <Pagination page={page} pageSize={pageSize} total={rows.length} onPageChange={goTo} />}
        </div>
    );
}
